import {FC, useEffect, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {Box, Typography, useMediaQuery} from '@mui/material';
import Button from '@components/Button';
import {ERROR, H5} from '@/utils/constants';
import Modal from '@/shared/components/Modal';
import {ReactComponent as DangerIcon} from '@icons/danger.svg';
import {ReactComponent as PaymentIcon} from '@icons/payment-method.svg';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getUserInfo, getUserSelector} from '@/store/user';
import Loader from '@/shared/components/Loader';
import {profileContactList} from './ProfileGeneral.style';
import ChangePasswordModal from './ChangePasswordModal';
import ChangePhotoModal from './ChangePhotoModal';
import ProfileForm from './ProfileForm';
import {PAYMENT_METHOD} from '@/utils/enums';
import PaymentOptions from './PaymentOptions';
import {OrganizationAttendeesService} from '@/utils/services';
import {getErrorMessage} from '@/utils/helpers';
import {toast} from 'react-toastify';
import {
  clearCardSetupRedirect,
  hasPendingCardSetup,
  useCardAttachment,
  WaitResult,
} from '@/utils/hooks/useCardAttachment';
import {getCurrentEventSelector} from '@/store/organization-event';
import axios from 'axios';

const getCardErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    if ((error.response?.status ?? 0) >= 500) {
      return 'Something went wrong on our side. Please try again later.';
    }
    return getErrorMessage(error);
  }
  return error instanceof Error ? error.message : 'Something went wrong. Please try again later.';
};

const ProfileGeneral: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  const {eventSlug} = useParams();

  const user = useAppSelector(getUserSelector);
  const event = useAppSelector(getCurrentEventSelector);

  const {isStarting, isWaiting, refresh, waitForAttachment, startAttachment} = useCardAttachment(
    event?.id,
    location.pathname
  );

  const [isShowDeleteAvatarModal, setIsShowDeleteAvatarModal] = useState(false);
  const [isShowDeleteAccountModal, setIsShowDeleteAccountModal] = useState(false);
  const [isShowChangePasswordModal, setIsShowChangePasswordModal] = useState(false);
  const [isShowChangePhotoModal, setIsShowChangePhotoModal] = useState(false);
  const [isShowPaymentModal, setIsShowPaymentModal] = useState(false);
  const [paymentStep, setPaymentStep] = useState<1 | 2>(1);
  const [paymentOption, setPaymentOption] = useState(
    user?.paymentMethod ? user.paymentMethod : PAYMENT_METHOD.CREDIT_CARD
  );

  const profilePath = `/${eventSlug}/profile/general`;

  const handleDeleteAvatarModalChange = () => setIsShowDeleteAvatarModal(!isShowDeleteAvatarModal);
  const handleDeleteAccountModalChange = () => setIsShowDeleteAccountModal(!isShowDeleteAccountModal);
  const handleChangePasswordModalChange = () => setIsShowChangePasswordModal(!isShowChangePasswordModal);
  const handleChangePhotoModalChange = () => setIsShowChangePhotoModal(!isShowChangePhotoModal);

  const isMobile = useMediaQuery('(max-width: 639.98px)');

  const deleteAvatar = () => {
    handleDeleteAvatarModalChange();
  };

  const deleteAccount = () => {
    handleDeleteAccountModalChange();
  };

  // ---------- Payment: старе ----------

  const handleOpenPaymentModal = () => {
    setPaymentStep(user?.paymentMethod ? 2 : 1);
    setIsShowPaymentModal(true);
  };

  const handleClosePaymentModal = () => {
    setIsShowPaymentModal(false);
    setPaymentStep(1);
  };

  const handleNextStep = () => setPaymentStep(2);

  const addPaymentOption = async () => {
    if (!user?.bidderId) return;
    try {
      const res = await OrganizationAttendeesService.addAttendeePaymentStatus(user.bidderId, paymentOption);
      if (res.status === 204) {
        await dispatch(getUserInfo(user?.id)).unwrap();
        toast.success('Payment method added successfully!');
      }
      handleClosePaymentModal();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  // ---------- Payment: Stripe-флоу для картки ----------

  const syncUser = async () => {
    if (user?.id) await dispatch(getUserInfo(user.id)).unwrap();
  };

  const handleWaitResult = async (result: WaitResult) => {
    if (result === 'attached') {
      await syncUser();
      toast.success('Payment method added successfully!');
    }
    if (result === 'timeout') {
      toast.error('Card verification is taking longer than expected. Please refresh the page in a moment.', {
        toastId: 'card-timeout',
        autoClose: 8000,
      });
    }
  };

  const addCardViaStripe = async () => {
    try {
      const result = await startAttachment();
      if (result === 'redirect') return;

      handleClosePaymentModal();

      if (result === 'attached') {
        await syncUser();
        toast.success('Payment method is already attached');
        return;
      }

      // processing
      await handleWaitResult(await waitForAttachment());
    } catch (error) {
      handleClosePaymentModal();
      toast.error(getCardErrorMessage(error), {toastId: 'card-setup-error', autoClose: 8000});
    }
  };

  const handleConfirmPayment = () =>
    paymentOption === PAYMENT_METHOD.CREDIT_CARD ? addCardViaStripe() : addPaymentOption();

  // Ініціалізація / повернення зі Stripe
  useEffect(() => {
    if (!event?.id) return;

    const init = async () => {
      try {
        if (location.pathname.endsWith('/setup-cancel')) {
          clearCardSetupRedirect(event.id);
          await refresh();
          toast.warning('Card setup was cancelled or failed. Please try again.', {
            toastId: 'card-setup-cancel',
            autoClose: 8000,
          });
          navigate(profilePath, {replace: true});
          return;
        }
        const returnedFromSetup = hasPendingCardSetup(event.id);

        const attached = await refresh();

        if (attached) {
          if (returnedFromSetup) {
            await syncUser();
            toast.success('Payment method added successfully!', {toastId: 'card-attached'});
          }
          return;
        }

        if (returnedFromSetup) {
          await handleWaitResult(await waitForAttachment());
        }
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id]);

  // Тост обробки картки
  useEffect(() => {
    if (!isWaiting) return undefined;

    const toastId = toast.loading('We are processing your card. This may take a few seconds...');

    return () => {
      toast.dismiss(toastId);
    };
  }, [isWaiting]);

  return (
    <Box sx={profileContactList.wrapper}>
      {user ? (
        <ProfileForm user={user} />
      ) : (
        <Box sx={profileContactList.loaderWrap}>
          <Loader size={50} />
        </Box>
      )}

      {!isMobile && (
        <Box sx={profileContactList.widget}>
          <Box>
            <Typography variant={H5} sx={profileContactList.widgetTitle}>
              {user?.paymentMethod ? 'Update Payment Method' : 'Add Payment Method'}
            </Typography>
            <Typography sx={profileContactList.secondaryText}>
              {user?.paymentMethod
                ? 'Update your payment method at any time.'
                : 'Adding a payment method allows you to participate in the auction and make donations.'}
            </Typography>
          </Box>
          <Box>
            <Button
              variant="text"
              sx={profileContactList.btn}
              onClick={handleOpenPaymentModal}
              disabled={isStarting || isWaiting}
              title={user?.paymentMethod ? 'Update Payment Method' : 'Add Payment Method'}
            />
          </Box>
        </Box>
      )}

      {!isMobile && (
        <Box sx={profileContactList.widget}>
          <Box>
            <Typography variant={H5} sx={profileContactList.widgetTitle}>
              Password
            </Typography>
            <Typography sx={profileContactList.secondaryText}>Update your password at any time.</Typography>
          </Box>
          <Box>
            <Button
              variant="text"
              sx={profileContactList.btn}
              onClick={handleChangePasswordModalChange}
              title="Change password"
            />
          </Box>
        </Box>
      )}

      <Box sx={profileContactList.widget}>
        <Box>
          <Typography variant={H5} sx={profileContactList.widgetTitle}>
            Delete Account
          </Typography>
          <Typography sx={profileContactList.secondaryText}>
            Permanently delete your account and all associated data. This action cannot be undone.
          </Typography>
        </Box>
        <Box>
          <Button
            color="error"
            variant="text"
            sx={[profileContactList.btn, profileContactList.red]}
            onClick={handleDeleteAccountModalChange}
            title="Delete account"
          />
        </Box>
      </Box>

      <Modal
        closeButtonTitle="Cancel"
        mainButtonTitle="Delete"
        onClose={handleDeleteAccountModalChange}
        onSave={deleteAccount}
        open={isShowDeleteAccountModal}
        sx={profileContactList.modal}
        title="Delete Account"
        description="Are you sure you want to delete?"
        mainIcon={<DangerIcon />}
        color={ERROR}
        isShowCloseButton
        isShowSaveButton
      />
      <Modal
        closeButtonTitle="Cancel"
        mainButtonTitle="Delete"
        onClose={handleDeleteAvatarModalChange}
        onSave={deleteAvatar}
        open={isShowDeleteAvatarModal}
        sx={profileContactList.modal}
        title="Delete Photo"
        description="Are you sure you want to delete?"
        mainIcon={<DangerIcon />}
        color={ERROR}
        isShowCloseButton
        isShowSaveButton
      />

      <Modal onClose={handleChangePasswordModalChange} open={isShowChangePasswordModal} sx={profileContactList.modal}>
        <ChangePasswordModal onCloseModal={handleChangePasswordModalChange} />
      </Modal>
      <Modal onClose={handleChangePhotoModalChange} open={isShowChangePhotoModal} sx={profileContactList.customeModal}>
        <ChangePhotoModal onCloseModal={handleChangePhotoModalChange} />
      </Modal>

      {/* Крок 1 / Крок 2 вибору способу оплати */}
      {paymentStep === 1 ? (
        <Modal
          open={isShowPaymentModal}
          onClose={handleClosePaymentModal}
          title="Add Payment Method"
          description="Adding a payment method allows you to participate in the auction and make donations."
          isShowCloseButton
          closeButtonTitle="Later"
          mainButtonTitle="Add Payment Method"
          isShowSaveButton
          onSave={handleNextStep}
          mainIcon={<PaymentIcon />}
          sx={profileContactList.modal}
        />
      ) : (
        <Modal
          open={isShowPaymentModal}
          onClose={handleClosePaymentModal}
          title="Choose Payment Option"
          isShowCloseButton
          closeButtonTitle="Cancel"
          mainButtonTitle="Confirm"
          isShowSaveButton
          onSave={handleConfirmPayment}
          sx={profileContactList.modal}
        >
          <PaymentOptions value={paymentOption} onChange={setPaymentOption} />
        </Modal>
      )}
    </Box>
  );
};

export default ProfileGeneral;
